import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { deleteMessage, getMessages, getRoster, postMentionsSeen, postMessage } from '../core/api';
import { useT } from '../core/lexicon';
import { clock, dayLabel } from '../core/util';
import { Avatar } from '../core/sprite';
import { EMOJI } from '../core/emoji';
import { activeMention, renderWithMentions } from '../core/mentions';
import { useApp } from '../App';
import { Screen, TopBar } from './Layout';
import type { ChatMessage, RosterMember } from '../core/types';

const PAGE = 50;

/**
 * The league chat (spec §20, api.md). One conversation, shared by every style.
 * Newest-first from the API, rendered oldest→newest and paged backwards. It never
 * carries submitter identity, another player's ballot, or a running score — those
 * are simply not in the message body, and there is no "share my ballot" here.
 */
export function Chat() {
  const t = useT();
  const { user } = useApp();
  const [msgs, setMsgs] = useState<ChatMessage[]>([]); // ascending (oldest first)
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [roster, setRoster] = useState<Map<number, RosterMember>>(new Map());
  const logRef = useRef<HTMLDivElement>(null);
  const stickBottom = useRef(true);
  // Composer helpers: an emoji picker and an @-mention autocomplete.
  const taRef = useRef<HTMLTextAreaElement>(null);
  const caretRef = useRef<number | null>(null);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [mQuery, setMQuery] = useState<string | null>(null);
  const [mAt, setMAt] = useState(0);
  const [mIndex, setMIndex] = useState(0);

  const rosterNames = useMemo(() => [...roster.values()].map((m) => m.display_name), [roster]);
  const suggestions = useMemo(() => {
    if (mQuery === null) return [];
    const q = mQuery.toLowerCase();
    return [...roster.values()]
      .filter((m) => m.user_id !== user.user_id && m.display_name.toLowerCase().startsWith(q))
      .slice(0, 6);
  }, [mQuery, roster, user.user_id]);

  // The message payload only carries user_id; pull the roster once for real faces.
  useEffect(() => {
    getRoster()
      .then((list) => setRoster(new Map(list.map((m) => [m.user_id, m]))))
      .catch(() => {});
  }, []);

  // Opening the chat clears the unread-mention watermark; the menu badge reads 0
  // on its next poll. Fire-and-forget — it must never block the chat rendering.
  useEffect(() => {
    postMentionsSeen().catch(() => {});
  }, []);

  async function loadLatest() {
    setLoading(true);
    setProblem(null);
    try {
      const batch = await getMessages(undefined, PAGE); // newest first
      setMsgs([...batch].reverse());
      setHasMore(batch.length === PAGE);
    } catch (e) {
      setProblem((e as Error).message || 'Could not load the chat.');
    } finally {
      setLoading(false);
    }
  }

  async function loadOlder() {
    if (!msgs.length) return;
    const oldest = msgs[0].message_id;
    const batch = await getMessages(oldest, PAGE);
    stickBottom.current = false;
    setMsgs((prev) => [...[...batch].reverse(), ...prev]);
    setHasMore(batch.length === PAGE);
  }

  useEffect(() => {
    loadLatest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useLayoutEffect(() => {
    if (stickBottom.current && logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [msgs]);

  // After an emoji/mention insert we changed `body` programmatically; put the
  // caret back where the user expects and keep focus in the textarea.
  useLayoutEffect(() => {
    if (caretRef.current !== null && taRef.current) {
      const c = caretRef.current;
      taRef.current.focus();
      taRef.current.setSelectionRange(c, c);
      caretRef.current = null;
    }
  }, [body]);

  // Recompute the active @-query from the caret position.
  function syncMention() {
    const ta = taRef.current;
    if (!ta) return;
    const m = activeMention(ta.value, ta.selectionStart ?? ta.value.length);
    if (m) {
      setMQuery(m.query);
      setMAt(m.at);
      setMIndex(0);
    } else {
      setMQuery(null);
    }
  }

  function insertAt(text: string, from: number, to: number) {
    setBody((prev) => prev.slice(0, from) + text + prev.slice(to));
    caretRef.current = from + text.length;
  }

  function addEmoji(e: string) {
    const ta = taRef.current;
    const from = ta?.selectionStart ?? body.length;
    const to = ta?.selectionEnd ?? body.length;
    insertAt(e, from, to);
    setEmojiOpen(false);
  }

  function pickMention(m: RosterMember) {
    const ta = taRef.current;
    const to = ta?.selectionStart ?? body.length;
    insertAt(`@${m.display_name} `, mAt, to);
    setMQuery(null);
  }

  async function send() {
    const v = body.trim();
    if (!v) return;
    setSending(true);
    setProblem(null);
    try {
      const created = await postMessage(v);
      setBody('');
      stickBottom.current = true;
      // Show it straight away from the POST response so it can't flicker out even
      // if the reload is briefly stale; loadLatest reconciles (dedup by id).
      if (created && created.message_id) {
        setMsgs((prev) => (prev.some((m) => m.message_id === created.message_id) ? prev : [...prev, created]));
      }
      await loadLatest();
    } catch (e) {
      setProblem((e as Error).message || 'Could not send your message.');
    } finally {
      setSending(false);
    }
  }

  async function remove(id: number) {
    await deleteMessage(id);
    setMsgs((prev) => prev.filter((m) => m.message_id !== id));
  }

  const participants = new Set(msgs.map((m) => m.user_id)).size;

  let lastDay = '';

  return (
    <Screen fixed>
      <TopBar title={t('chat.name')} sub={participants ? t('chat.count', { n: participants }).toUpperCase() : undefined} />
      <div className="warn">{t('chat.warning')}</div>

      <div className="log" ref={logRef}>
        {hasMore ? (
          <button className="load-more" onClick={loadOlder}>
            ▲ EARLIER
          </button>
        ) : null}
        {loading ? (
          <div className="spin" style={{ textAlign: 'center', padding: 20 }}>
            LOADING…
          </div>
        ) : problem ? (
          <div className="errline" style={{ margin: 12 }}>{problem}</div>
        ) : msgs.length === 0 ? (
          <div className="empty">
            <h3>NOTHING YET</h3>
            <p>{t('chat.prompt')}.</p>
          </div>
        ) : (
          msgs.map((m) => {
            const day = dayLabel(m.created_at);
            const showDay = day !== lastDay;
            lastDay = day;
            const self = m.user_id === user.user_id;
            const mine = self || user.is_admin;
            return (
              <div key={m.message_id}>
                {showDay ? <div className="day">{day}</div> : null}
                <div className={`cmsg${self ? ' self' : ''}`}>
                  {!self ? <Avatar spriteData={roster.get(m.user_id)?.sprite_data ?? null} seed={roster.get(m.user_id)?.sprite_key || String(m.user_id)} size={36} /> : null}
                  <div className="body">
                    <span className="who">
                      {m.display_name.toUpperCase()}
                      <span className="when">{clock(m.created_at)}</span>
                    </span>
                    <div className="txt">{renderWithMentions(m.body, rosterNames)}</div>
                    {mine ? (
                      <div>
                        <a
                          style={{ fontSize: 11, color: 'var(--k2)', cursor: 'pointer' }}
                          onClick={() => remove(m.message_id)}
                        >
                          delete
                        </a>
                      </div>
                    ) : null}
                  </div>
                  {self ? <Avatar spriteData={roster.get(m.user_id)?.sprite_data ?? null} seed={roster.get(m.user_id)?.sprite_key || String(m.user_id)} size={36} /> : null}
                </div>
              </div>
            );
          })
        )}
      </div>

      <div className="compose">
        {emojiOpen ? (
          <div className="emoji-pop" role="listbox" aria-label="Emoji">
            {EMOJI.map((e) => (
              <button key={e} type="button" className="emoji-cell" onClick={() => addEmoji(e)}>
                {e}
              </button>
            ))}
          </div>
        ) : null}
        {mQuery !== null && suggestions.length ? (
          <div className="mention-pop" role="listbox" aria-label="Mention">
            {suggestions.map((m, i) => (
              <button
                key={m.user_id}
                type="button"
                className={`mention-row${i === mIndex ? ' on' : ''}`}
                onMouseDown={(ev) => {
                  ev.preventDefault();
                  pickMention(m);
                }}
              >
                <Avatar spriteData={m.sprite_data} seed={m.sprite_key || String(m.user_id)} size={22} />
                <span>{m.display_name}</span>
              </button>
            ))}
          </div>
        ) : null}
        <button
          type="button"
          className="emoji-btn"
          aria-label="Emoji"
          onClick={() => {
            setEmojiOpen((v) => !v);
            setMQuery(null);
          }}
        >
          😊
        </button>
        <textarea
          ref={taRef}
          value={body}
          maxLength={400}
          rows={2}
          placeholder={t('chat.prompt')}
          onChange={(e) => {
            setBody(e.target.value);
            setEmojiOpen(false);
            syncMention();
          }}
          onClick={syncMention}
          onKeyUp={(e) => {
            if (e.key.startsWith('Arrow')) syncMention();
          }}
          onKeyDown={(e) => {
            const open = mQuery !== null && suggestions.length > 0;
            if (open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
              e.preventDefault();
              setMIndex((i) => (e.key === 'ArrowDown' ? (i + 1) % suggestions.length : (i - 1 + suggestions.length) % suggestions.length));
              return;
            }
            if (open && (e.key === 'Enter' || e.key === 'Tab')) {
              e.preventDefault();
              pickMention(suggestions[mIndex]);
              return;
            }
            if (e.key === 'Escape') {
              setMQuery(null);
              setEmojiOpen(false);
              return;
            }
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
        />
        <button disabled={!body.trim() || sending} onClick={send}>
          SEND
        </button>
      </div>
    </Screen>
  );
}
